import { ApiError, type AuthenticatedTransport, requestJson } from "@groundspark/browser-auth";
import { parseCanvassingProjects, type CanvassingProject } from "./canvassing";
import { parseConstituencyResults, type ConstituencyElectionResult } from "./elections";
import { parseCouncilAreaResults, type CouncilAreaResults } from "./council-elections";
import type { HoveredMapArea } from "./map-view";
import {
  type AffinityRecipe,
  type AnalysisModelId,
  type AreaGeography,
  API_VERSION,
  type CatalogResponse,
  type ElectoralRegisterBootstrapResponse,
  type MapMetadataResponse,
  type MapAreasResponse,
  type MetricRequest,
  type MetricResponse,
  type OperationalModelParams,
  parseCatalogResponse,
  parseElectoralRegisterBootstrapResponse,
  parseMapMetadataResponse,
  parseMapAreasResponse,
  parseMetricResponse,
} from "./domain";

export interface CancellableMetricRequest {
  readonly generation: number;
  readonly response: Promise<MetricResponse>;
  readonly signal: AbortSignal;
  cancel(reason?: unknown): void;
}

export class IntelligenceApi {
  public async mapAreas(
    model: AnalysisModelId,
    geography: AreaGeography,
    signal?: AbortSignal,
  ): Promise<MapAreasResponse> {
    const result = await requestJson(
      this.transport,
      `/v1/map-areas/${model}/${geography}`,
      parseMapAreasResponse,
      "map areas",
      { signal },
    );
    if (result.model !== model) {
      throw new ApiError(
        "The intelligence server returned areas for the wrong analysis model.",
      );
    }
    return result;
  }
  public councilResults(
    area: HoveredMapArea,
    signal?: AbortSignal,
  ): Promise<CouncilAreaResults> {
    return requestJson(
      this.transport,
      `/v1/elections/council/${area.geography}/${area.featureId}`,
      parseCouncilAreaResults,
      "local election results",
      { signal },
    );
  }
  public parliamentResults(
    signal?: AbortSignal,
  ): Promise<readonly ConstituencyElectionResult[]> {
    return requestJson(
      this.transport,
      "/v1/elections/parliament",
      parseConstituencyResults,
      "parliamentary election results",
      { signal },
    );
  }
  public canvassingProjects(signal?: AbortSignal): Promise<readonly CanvassingProject[]> {
    return requestJson(
      this.transport,
      "/v1/canvassing-projects",
      parseCanvassingProjects,
      "canvassing projects",
      { signal },
    );
  }
  private metricGeneration = 0;
  private readonly metricClientId: string;
  private expectedBaseAreaCount: number | null = null;
  private readonly expectedModelCounts = new Map<AnalysisModelId, number>();

  public constructor(
    private readonly transport: AuthenticatedTransport,
    metricClientId: string = createMetricClientId(),
  ) {
    if (metricClientId.length === 0 || metricClientId.length > 200) {
      throw new ApiError("The metric client identifier is invalid.");
    }
    this.metricClientId = metricClientId;
  }

  public async catalog(): Promise<CatalogResponse> {
    const catalog = await requestJson(
      this.transport,
      "/v1/catalog",
      parseCatalogResponse,
      "catalogue",
    );
    if (catalog.apiVersion !== API_VERSION) {
      throw new ApiError(
        `The intelligence server API version ${catalog.apiVersion} is incompatible with client version ${API_VERSION}.`,
      );
    }
    this.expectedBaseAreaCount = catalog.baseAreaCount;
    this.expectedModelCounts.clear();
    for (const model of catalog.analysisModels) {
      this.expectedModelCounts.set(model.id, model.baseAreaCount);
    }
    return catalog;
  }

  public async mapMetadata(
    model: AnalysisModelId,
    signal?: AbortSignal,
  ): Promise<MapMetadataResponse> {
    const metadata = await requestJson(
      this.transport,
      `/v1/map-metadata/${model}`,
      parseMapMetadataResponse,
      "map metadata",
      { signal },
    );
    if (metadata.model !== model) {
      throw new ApiError(
        "The intelligence server returned map metadata for the wrong analysis model.",
      );
    }
    const expected = this.expectedModelCounts.get(model);
    if (expected !== undefined) {
      requireLength(metadata.baseAreaMapIndices, expected, "base-area map indices");
    }
    const nationalCount = this.expectedBaseAreaCount;
    if (
      nationalCount !== null &&
      metadata.baseAreaMapIndices.some((index) => index >= nationalCount)
    ) {
      throw new ApiError(
        "The intelligence server returned an out-of-range base-area map index.",
      );
    }
    return metadata;
  }

  public async electoralRegisterBootstrap(
    registerId: string,
    signal?: AbortSignal,
  ): Promise<ElectoralRegisterBootstrapResponse> {
    const bootstrap = await requestJson(
      this.transport,
      `/v1/electoral-registers/${encodePathSegment(registerId, "electoral-register ID")}/bootstrap`,
      parseElectoralRegisterBootstrapResponse,
      "electoral-register bootstrap",
      { signal },
    );
    const expected = this.expectedBaseAreaCount;
    if (expected !== null) {
      requireLength(bootstrap.electorCountsByBaseArea, expected, "electoral-register counts");
    }
    return bootstrap;
  }

  public startMetricRequest(request: MetricRequest): CancellableMetricRequest {
    if (this.metricGeneration >= Number.MAX_SAFE_INTEGER) {
      throw new ApiError("The metric request generation is exhausted.");
    }
    const generation = ++this.metricGeneration;
    const controller = new AbortController();
    const response = requestJson(this.transport, "/v1/metrics", parseMetricResponse, "metric", {
      method: "POST",
      body: JSON.stringify({
        client_id: this.metricClientId,
        generation,
        request: encodeMetricRequest(request),
      }),
      signal: controller.signal,
    }).then((metric) => {
      if (metric.model !== request.model) {
        throw new ApiError(
          "The intelligence server returned a metric for the wrong analysis model.",
        );
      }
      const expected = this.expectedModelCounts.get(request.model);
      if (expected !== undefined)
        requireLength(metric.percentiles, expected, "metric percentiles");
      if (request.kind === "statistic" && metric.aggregationInputs === null) {
        throw new ApiError("The intelligence server omitted statistic aggregation inputs.");
      }
      const hasCompletionHours = metric.completionHours !== null;
      if ((request.kind === "operational_priority") !== hasCompletionHours) {
        throw new ApiError(
          request.kind === "operational_priority"
            ? "The intelligence server omitted operational completion hours."
            : "The intelligence server returned completion hours for a non-operational metric.",
        );
      }
      return metric;
    });
    return {
      generation,
      response,
      signal: controller.signal,
      cancel: (reason?: unknown) => controller.abort(reason),
    };
  }
}

function encodeMetricRequest(request: MetricRequest): unknown {
  switch (request.kind) {
    case "statistic":
      return { kind: request.kind, model: request.model, statistic_id: request.statisticId };
    case "affinity":
      return {
        kind: request.kind,
        model: request.model,
        recipe: encodeAffinityRecipe(request.recipe),
      };
    case "operational_priority":
      return {
        kind: request.kind,
        model: request.model,
        recipe: encodeAffinityRecipe(request.recipe),
        parameters: encodeOperationalModelParams(request.parameters),
      };
  }
}

function encodeAffinityRecipe(recipe: AffinityRecipe): unknown {
  return {
    groups: recipe.groups.map((group) => ({
      name: group.name,
      weight_percent: group.weightPercent,
      subgroups: group.subgroups.map((subgroup) => ({
        enabled: subgroup.enabled,
        statistic_ids: subgroup.statisticIds,
      })),
    })),
  };
}

function encodeOperationalModelParams(parameters: OperationalModelParams): unknown {
  return {
    service_seconds_per_household: parameters.serviceSecondsPerHousehold,
    walking_seconds_at_reference_density: parameters.walkingSecondsAtReferenceDensity,
    reference_density_households_per_km2: parameters.referenceDensityHouseholdsPerKm2,
    fixed_setup_seconds_per_base_area: parameters.fixedSetupSecondsPerBaseArea,
    affinity_contrast_p90_p10: parameters.affinityContrastP90P10,
    minimum_effective_density_households_per_km2:
      parameters.minimumEffectiveDensityHouseholdsPerKm2,
    maximum_effective_density_households_per_km2:
      parameters.maximumEffectiveDensityHouseholdsPerKm2,
  };
}

function encodePathSegment(value: string, name: string): string {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) || value.length > 64) {
    throw new ApiError(`The ${name} is invalid.`);
  }
  return encodeURIComponent(value);
}

function requireLength(values: readonly unknown[], expected: number, name: string): void {
  if (values.length !== expected) {
    throw new ApiError(
      `The intelligence server returned ${values.length} ${name} for ${expected} base areas.`,
    );
  }
}

function createMetricClientId(): string {
  return globalThis.crypto.randomUUID();
}
